import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@mui/material";
import { toast } from 'react-toastify';
import "./Editstuent.css";
import configData from "../../configure"; // Import the configData object

const BASE_URL = `${process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://org1.egurukulapps.com'}/campusidentity`; 

const EditStudent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState(null);

  // Initialize form state with all needed fields
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    lastFourDigitsOfAadhaarNumber: '',
    dateOfBirth: '',
    gender: '',
    placeOfBirth: '',
    bloodGroup: '',
    nationality: '',
    religion: '',
    category: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    pinCode: '',
    admissionId: '',
    rollNumber: '',
    previousSchoolDetails: '',
    scholarshipAwards: '',
    transportOpted: '',
    dateOfJoining: '',
    alternatePhoneNumber: ''
  });

  const userId = location.state?.userId;
  const selectedClass = location.state?.selectedClass;

  // API function to fetch student details
  const fetchStudentDetails = async (userId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${configData.GET_STUDENT_BY_ID}/${userId}`);
      setStudentData(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = 
        typeof error.response?.data === 'string'
          ? error.response.data
          : error.response?.data?.message || 'Failed to fetch student details';
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // API function to update student details
  const updateStudentDetails = async (userId, updatedData) => {
    setLoading(true);
    try {
      console.log('Sending data to server:', JSON.stringify(updatedData, null, 2));
      const response = await axios.put(
        `${configData.UPDATE_USER}/${userId}`,
        updatedData,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      return response.data;
    } catch (error) {
      console.log('Update error:', error.response?.data);
      const errorMessage = 
        typeof error.response?.data === 'string'
          ? error.response.data
          : error.response?.data?.message || 'Failed to update student details';
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format dates to ISO format if they exist
  const formatDateForBackend = (dateString) => {
    if (!dateString) return null;
    // Make sure it's a valid date
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  };

  // Fetch student data when component mounts
  useEffect(() => {
    if (userId) {
      fetchStudentDetails(userId)
        .then(data => {
          if (data) {
            setFormData({
              firstName: data.user.firstName || '',
              middleName: data.user.middleName || '',
              lastName: data.user.lastName || '',
              email: data.user.email || '',
              phoneNumber: data.user.phoneNumber || '',
              lastFourDigitsOfAadhaarNumber: data.user.lastFourDigitsOfAadhaarNumber || '',
              dateOfBirth: data.user.dateOfBirth
                ? new Date(data.user.dateOfBirth).toISOString().split('T')[0]
                : '',
              gender: data.user.gender || '',
              placeOfBirth: data.user.placeOfBirth || '',
              bloodGroup: data.user.bloodGroup || '',          // ← most important line
              nationality: data.user.nationality || '',
              religion: data.user.religion || '',
              category: data.user.category || '',
              addressLine1: data.user.addressDetails?.[0]?.addressLine1 || '',
              addressLine2: data.user.addressDetails?.[0]?.addressLine2 || '',
              city: data.user.addressDetails?.[0]?.city || '',
              state: data.user.addressDetails?.[0]?.state || '',
              country: data.user.addressDetails?.[0]?.country || 'India',
              pinCode: data.user.addressDetails?.[0]?.pinCode || '',
              admissionId: data.studentInfo?.admissionId || '',
              rollNumber: data.studentInfo?.rollNumber || '',
              previousSchoolDetails: data.studentInfo?.previousSchoolDetails || '',
              scholarshipAwards: data.studentInfo?.scholarshipAwards || '',
              transportOpted: data.studentInfo?.transportOpted ?? '', // ← better nullish
              dateOfJoining: data.studentInfo?.dateOfJoining
                ? new Date(data.studentInfo.dateOfJoining).toISOString().split('T')[0]
                : '',
              alternatePhoneNumber: data.user.addressDetails?.[0]?.alternatePhoneNumber || ''
            });
          }
        })
        .catch(err => {
          console.error('Error fetching student:', err);
        });
    }
  }, [userId]);

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'phoneNumber' || name === 'alternatePhoneNumber') {
      value = value.replace(/[^0-9]/g, '').slice(0, 10);
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanPhone = (formData.phoneNumber || '').replace(/\D/g, '');
    if (!cleanPhone) {
      toast.error("Phone number is required");
      return;
    } else if (cleanPhone.length !== 10) {
      toast.error("Phone number must be a 10-digit mobile number");
      return;
    }
    const cleanAltPhone = (formData.alternatePhoneNumber || '').replace(/\D/g, '');
    if (cleanAltPhone && cleanAltPhone.length !== 10) {
      toast.error("Alternate contact number must be a 10-digit mobile number");
      return;
    }

    // Prepare the updated data with properly formatted dates
    const updatedData = {
      user: {
        ...studentData.user,
        userId: userId,
        firstName: formData.firstName,
        middleName: formData.middleName,

        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        lastFourDigitsOfAadhaarNumber: formData.lastFourDigitsOfAadhaarNumber,
        dateOfBirth: formatDateForBackend(formData.dateOfBirth),
        gender: formData.gender,
        placeOfBirth: formData.placeOfBirth,
        bloodGroup: formData.bloodGroup,
        nationality: formData.nationality,
        religion: formData.religion,
        category: formData.category,
        alternatePhoneNumber: formData.alternatePhoneNumber,
        addressDetails: [ {
          ...(studentData.user.addressDetails || {}),
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          pinCode: formData.pinCode,
          alternatePhoneNumber: formData.alternatePhoneNumber,

        }],
      },
      studentInfo: {
        ...(studentData.studentInfo || {}),
        userId: userId,
        admissionId: formData.admissionId,
        rollNumber: formData.rollNumber,
        previousSchoolDetails: formData.previousSchoolDetails,
        scholarshipAwards: formData.scholarshipAwards,
        transportOpted: formData.transportOpted,
        dateOfJoining: formatDateForBackend(formData.dateOfJoining)
      }
    };

    try {
        await updateStudentDetails(userId, updatedData);
        toast.success("Student details updated successfully!");
        navigate('/students', { 
          replace: true,
          state: { selectedClass, refreshList: true } 
        });
    } catch (error) {
      console.error('Failed to update student:', error);
      // Error is already set in the updateStudentDetails function
    }
  };

  const handleCancel = () => {
    navigate('/students', { 
      replace: true,
      state: { selectedClass: selectedClass } 
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="registration-container">
      <div className="header-container">
        <button onClick={() => navigate('/students')} className="back-button">
          Back
        </button>
        <h2>Edit Student</h2>
      </div>
      <form onSubmit={handleSubmit} className="registration-form">
        <h3>Basic Information</h3>

        <div className="basicinfo">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">
                First Name: <span className="required-star">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First Name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="firstName">
                Middle Name:
              </label>
              <input
                type="text"
                id="middleName"
                name="middleName"
                value={formData.middleName}
                onChange={handleChange}
                placeholder="Middle Name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">
                Last Name: <span className="required-star">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last Name"
                required
              />
            </div>

           
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gender">Gender:</label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Select Gender</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="dateOfBirth">
                Date of Birth: <span className="required-star">*</span>
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
  <label htmlFor="bloodGroup">
    Blood Group{formData.bloodGroup === "" ? <span className="required-star">*</span> : ""}:
  </label>
  <select
    id="bloodGroup"
    name="bloodGroup"
    value={formData.bloodGroup || ""}
    onChange={handleChange}
    required   // ← add this if it's mandatory
  >
    <option value="" disabled>
      Select Blood Group
    </option>
    <option value="A_POSITIVE">A+</option>
    <option value="A_NEGATIVE">A-</option>
    <option value="B_POSITIVE">B+</option>
    <option value="B_NEGATIVE">B-</option>
    <option value="O_POSITIVE">O+</option>
    <option value="O_NEGATIVE">O-</option>
    <option value="AB_POSITIVE">AB+</option>
    <option value="AB_NEGATIVE">AB-</option>
  </select>
</div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nationality">Nationality:</label>
              <input
                type="text"
                id="nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                placeholder="Nationality"
              />
            </div>

            <div className="form-group">
              <label htmlFor="religion">Religion:</label>
              <input
                type="text"
                id="religion"
                name="religion"
                value={formData.religion}
                onChange={handleChange}
                placeholder="Religion"
              />
            </div>
            <div className="form-group">
  <label htmlFor="rollNumber">Roll Number:</label>
  <input
    type="text"
    id="rollNumber"
    name="rollNumber"
    value={formData.rollNumber}
    onChange={handleChange}
    placeholder="Roll Number"
  />
</div>
            
          </div>

          <div className="form-row">
          <div className="form-group">
              <label htmlFor="admissionId">Admission Number:</label>
              <input
                type="text"
                id="admissionId"
                name="admissionId"
                value={formData.admissionId}
                onChange={handleChange}
                placeholder="Admission Number"
              />
            </div>
          <div className="form-group">
              <label htmlFor="dateOfJoining">
                Admission Date: <span className="required-star">*</span>
              </label>
              <input
                type="date"
                id="dateOfJoining"
                name="dateOfJoining"
                value={formData.dateOfJoining}
                onChange={handleChange}
              />
            </div>
          </div>
   
        </div>
        <h3>Contact Information</h3>
        <div className="contactinfo">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="addressLine1">Address Line 1:</label>
              <input
                type="text"
                id="addressLine1"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleChange}
                placeholder="Address Line 1"
              />
            </div>
            <div className="form-group">
              <label htmlFor="addressLine2">Address Line 2:</label>
              <input
                type="text"
                id="addressLine2"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleChange}
                placeholder="Address Line 2"
              />
            </div>
            <div className="form-group">
              <label htmlFor="city">
                City: <span className="required-star">*</span>
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="state">State:</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="Enter State"
              />
            </div>
            <div className="form-group">
              <label htmlFor="pinCode">Pin Code:</label>
              <input
                type="text"
                id="pinCode"
                name="pinCode"
                value={formData.pinCode}
                onChange={handleChange}
                placeholder="Pin Code"
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Gmail: </label>
              <input
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@gmail.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number:</label>
              <input
                type="text"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Phone Number"
              />
            </div>
            <div className="form-group">
              <label htmlFor="alternatePhoneNumber">
                Alternate Contact Number:
              </label>
              <input
                type="text"
                id="alternatePhoneNumber"
                name="alternatePhoneNumber"
                value={formData.alternatePhoneNumber}
                onChange={handleChange}
                placeholder="Alternate Contact Number"
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastFourDigitsOfAadhaarNumber">Aadhaar</label>
              <input
                type="text"
                id="lastFourDigitsOfAadhaarNumber"
                name="lastFourDigitsOfAadhaarNumber"
                value={formData.lastFourDigitsOfAadhaarNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(-4);
                  handleChange({
                    target: { name: 'lastFourDigitsOfAadhaarNumber', value },
                  });
                }}
                placeholder="Last Four Digits of Aadhaar"
                maxLength="4"
              />
            </div>
          </div>
        </div>

        <h3>Other Details</h3>
        <div className="academic-details">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="previousSchoolDetails">
                Previous School Details:
              </label>
              <input
                type="text"
                id="previousSchoolDetails"
                name="previousSchoolDetails"
                value={formData.previousSchoolDetails}
                onChange={handleChange}
                placeholder="Previous School Details"
              />
            </div>

            <div className="form-group">
              <label htmlFor="scholarshipAwards">Scholarship Awards:</label>
              <input
                type="text"
                id="scholarshipAwards"
                name="scholarshipAwards"
                value={formData.scholarshipAwards}
                onChange={handleChange}
                placeholder="Scholarship Awards"
              />
            </div>

            <div className="form-group">
              <label htmlFor="transportOpted">Transport Opted:</label>
              <select
  id="transportOpted"
  name="transportOpted"
  value={formData.transportOpted}
  onChange={handleChange}
>
  <option value="">Select</option>
  <option value={true}>Yes</option>
  <option value={false}>No</option>
</select>

            </div>
          </div>
        </div>

        <div className="form-row-button-group">
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            Update Student
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditStudent;